import React, { useState } from 'react';
import { LinkIcon, PlusIcon, FileTextIcon, ScaleIcon, XIcon, GitPullRequestIcon, ListChecksIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Header } from '../components/Header';
import { copy } from '../copy';
import { ConfidenceBand } from '../components/discovery/ConfidenceBand';
import { StatusPill } from '../components/discovery/StatusPill';
import { Provenance } from '../components/discovery/Provenance';
type Gap = (typeof copy.completeness.referentialGaps)[number];
export function CompletenessPage() {
  const c = copy.completeness;
  const [request, setRequest] = useState<Gap[]>([]);
  const [draft, setDraft] = useState('');
  const addGap = (gap: Gap) => {
    if (request.find(g => g.id === gap.id)) return;
    setRequest(prev => [...prev, gap]);
    setDraft(prev => prev + (prev ? '\n' : '') + `• ${gap.description} (Ref: ${gap.source})`);
  };
  const removeGap = (id: string) => setRequest(prev => prev.filter(g => g.id !== id));
  const renderGap = (gap: Gap) => <div key={gap.id} className="rounded-2xl border border-slate-200 dark:border-[#333] bg-white dark:bg-[#1a1a1a] p-4 space-y-3" data-id="element-265">
      <div className="flex items-start justify-between gap-3" data-id="element-266">
        <p className="text-sm text-slate-700 dark:text-[#e5e5e5] leading-relaxed" data-id="element-267">
          {gap.description}
        </p>
        <StatusPill label={c.gapType[gap.type as keyof typeof c.gapType]} tone={gap.type === 'referential' ? 'purple' : 'blue'} data-id="element-268" />
      </div>
      <div className="flex items-center justify-between gap-3 flex-wrap" data-id="element-269">
        <Provenance source={gap.source} data-id="element-270" />
        <div className="flex items-center gap-3" data-id="element-271">
          <span className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-[#555]" data-id="element-272">
            Severity
            <ConfidenceBand value={gap.severity} compact data-id="element-273" />
          </span>
          <button onClick={() => addGap(gap)} disabled={!!request.find(g => g.id === gap.id)} className="flex items-center gap-1 rounded-lg border border-slate-200 dark:border-[#333] px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-[#a3a3a3] hover:bg-slate-50 dark:hover:bg-white/5 disabled:opacity-40" data-id="element-274">
            <PlusIcon size={12} data-id="element-275" />
            {c.addToRequest}
          </button>
        </div>
      </div>
    </div>;
  return <div className="min-h-screen bg-white dark:bg-[#1a1a1a] text-slate-900 dark:text-[#e5e5e5] transition-colors duration-200" data-id="element-276">
      <Header title={c.headerTitle} minimal showBackArrow backTo="/case" data-id="element-277" />

      <main className="mx-auto max-w-6xl px-6 py-12" data-id="element-278">
        <div className="mb-8" data-id="element-279">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white" data-id="element-280">
            {c.title}
          </h1>
          <p className="text-slate-500 dark:text-[#a3a3a3] mt-1" data-id="element-281">
            {c.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-282">
          <div className="lg:col-span-2 space-y-8" data-id="element-283">
            <section className="space-y-3" data-id="element-284">
              <h2 className="flex items-center gap-2 font-semibold text-slate-900 dark:text-white" data-id="element-285">
                <LinkIcon size={16} className="text-purple-500" data-id="element-286" />
                {c.referentialTitle}
              </h2>
              {c.referentialGaps.map(renderGap)}
            </section>
            <section className="space-y-3" data-id="element-287">
              <h2 className="flex items-center gap-2 font-semibold text-slate-900 dark:text-white" data-id="element-288">
                <GitPullRequestIcon size={16} className="text-blue-500" data-id="element-289" />
                {c.sequenceTitle}
              </h2>
              {c.sequenceGaps.map(renderGap)}
            </section>
          </div>

          {/* Supplemental request composer */}
          <aside className="lg:col-span-1" data-id="element-290">
            <div className="sticky top-20 rounded-3xl border border-slate-200 dark:border-[#333] bg-slate-50 dark:bg-[#222] p-5 space-y-4" data-id="element-291">
              <div data-id="element-292">
                <h2 className="flex items-center gap-2 font-semibold text-slate-900 dark:text-white" data-id="element-293">
                  <ListChecksIcon size={16} className="text-emerald-500" data-id="element-294" />
                  {c.composerTitle}
                </h2>
                <p className="text-xs text-slate-500 dark:text-[#a3a3a3] mt-1" data-id="element-295">
                  {c.composerSubtitle}
                </p>
              </div>

              <AnimatePresence data-id="element-296">
                {request.map(g => <motion.div key={g.id} initial={{
                opacity: 0,
                x: 8
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: 8
              }} className="flex items-start gap-2 rounded-xl border border-slate-200 dark:border-[#333] bg-white dark:bg-[#1a1a1a] p-2.5" data-id="element-297">
                    <span className="flex-1 text-xs text-slate-600 dark:text-[#a3a3a3] line-clamp-2" data-id="element-298">
                      {g.description}
                    </span>
                    <button onClick={() => removeGap(g.id)} className="text-slate-400 hover:text-red-500" data-id="element-299">
                      <XIcon size={14} data-id="element-300" />
                    </button>
                  </motion.div>)}
              </AnimatePresence>

              <textarea value={draft} onChange={e => setDraft(e.target.value)} rows={6} placeholder="Selected gaps assemble here as an editable draft..." className="w-full rounded-xl border border-slate-200 dark:border-[#333] bg-white dark:bg-[#1a1a1a] p-3 text-sm text-slate-700 dark:text-[#e5e5e5] outline-none focus:ring-2 focus:ring-slate-200 dark:focus:ring-slate-800 resize-none" data-id="element-301" />

              <div className="flex gap-2" data-id="element-302">
                <button disabled={request.length === 0} className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-slate-900 dark:bg-white px-3 py-2.5 text-sm font-medium text-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-slate-100 disabled:opacity-40 transition-all" data-id="element-303">
                  <FileTextIcon size={14} data-id="element-304" />
                  {c.exportLetter}
                </button>
                <button disabled={request.length === 0} className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 dark:border-[#333] px-3 py-2.5 text-sm font-medium text-slate-600 dark:text-[#a3a3a3] hover:bg-white dark:hover:bg-[#1a1a1a] disabled:opacity-40 transition-all" data-id="element-305">
                  <ScaleIcon size={14} data-id="element-306" />
                  {c.exportMotion}
                </button>
              </div>
            </div>
          </aside>
        </div>
      </main>
    </div>;
}